import isTouchDevice, { dragEvent } from "@/utils/isTouchDevice";
import classNames from "classnames";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import HBIcon from "../HBIcon";
import styles from "./index.module.less";
/**
 * @param {{
 * children?: any;
 * type?: string;
 * padding?: number;
 * iconClassName?: string;
 * className?: string;
 * contentClassName?: string;
 * style?: any;
 * hideControls?: boolean; // 隐藏所有控制元素
 * hideGradient?: boolean; // 保留按钮，隐藏渐变遮罩
 * onScroll?: (left: number) => void; // 滚动时回调
 * onScrollEnd?: () => void; // 滚动结束时回调
 * }}
 */
function HBSwiper(
  {
    children,
    type = "none",
    padding = 60,
    className,
    iconClassName,
    contentClassName,
    style,
    hideControls = false,
    hideGradient = false,
    onScroll,
    onScrollEnd,
    ...rest
  },
  ref
) {
  // 滚动位置
  const [start, setStart] = useState(false);
  const [end, setEnd] = useState(false);
  const viewRef = useRef();
  const contentRef = useRef();
  const [dragging, setDragging] = useState(false);
  const cancelAnimationRef = useRef(null);
  const positionRef = useRef(0);

  const setSwiperStatus = useCallback((left = 0) => {
    const viewDom = viewRef.current;
    const contentDom = contentRef.current;
    if (viewDom && contentDom) {
      const viewWidth = viewDom.offsetWidth; // 可视区域宽度
      const contentWidth = contentDom.offsetWidth; // 内容总宽度

      const realLeft = left || positionRef.current;

      setStart(realLeft < 0);
      setEnd(realLeft + contentWidth > viewWidth);
    }
  }, []);

  // 滚动到指定位置，使选中项居中
  const onGoPage = ({ index }) => {
    const viewDom = viewRef.current;
    const contentDom = contentRef.current;
    if (viewDom && contentDom) {
      const viewWidth = viewDom.offsetWidth; // 可视区域宽度
      const viewCenter = viewWidth / 2; // 视窗中心位置

      // 获取指定 index 的子元素
      const targetChild = contentDom.children[index];
      if (!targetChild) {
        console.warn(`Child at index ${index} not found`);
        return;
      }

      // 计算目标元素在 content 中的中心位置（相对于 content 左边缘）
      const itemLeft = targetChild.offsetLeft; // item 左边缘距离 content 左边缘的距离
      const itemWidth = targetChild.offsetWidth; // item 的实际宽度
      const itemCenterInContent = itemLeft + itemWidth / 2; // item 中心在 content 中的位置

      // 要让 item 居中，content 需要的 translateX 值
      // viewCenter - itemCenterInContent 表示 item 中心需要移动到视窗中心的偏移量
      const targetTranslateX = viewCenter - itemCenterInContent;

      // 计算从当前位置到目标位置需要移动的距离
      const currentPosition = positionRef.current;
      const distance = targetTranslateX - currentPosition;

      onSwitch({
        distance,
        duration: 300,
      });
    }
  };

  const setPosition = useCallback(
    (left = 0) => {
      if (contentRef.current) {
        contentRef.current.style.transform = `translate(${left}px, 0)`;
        const oldLeft = positionRef.current;
        positionRef.current = left;
        setSwiperStatus(left);
        onScroll?.(left);

        const viewWidth = viewRef.current?.offsetWidth;
        const contentWidth = contentRef.current?.offsetWidth;
        const maxLeft = 0;
        const minLeft = -(contentWidth - viewWidth);
        const isEnd = left >= maxLeft || left <= minLeft;
        if (isEnd && oldLeft !== left) {
          onScrollEnd?.();
        }
      }
    },
    [setSwiperStatus, onScroll, onScrollEnd]
  );

  const onSwitch = useCallback(
    ({ distance, direction = 1, duration = 300 }) => {
      const viewDom = viewRef.current;
      const contentDom = contentRef.current;

      if (viewDom && contentDom) {
        const maxLeft = 0;
        const minLeft = -(contentDom.offsetWidth - viewDom.offsetWidth);

        const maxScroll = contentDom.offsetWidth - viewDom.offsetWidth;
        const viewWidth = viewDom.offsetWidth; // 视图宽度
        const contentWidth = contentDom.offsetWidth; // 内容宽度
        distance = distance ?? Math.ceil(viewWidth); // 每次移动距离

        let left = positionRef.current; // 已经移动距离
        // const maxX = contentWidth - viewWidth; // 最大可移动距离

        let changeX = direction * distance;
        changeX = Math.min(maxLeft - left, Math.max(minLeft - left, changeX));

        const startTime = performance.now();

        let cancelLabel = null;
        const step = function () {
          // 距离目标滚动距离
          const rate = Math.min(1, (performance.now() - startTime) / duration); // 时间进度（0 - 1）

          const resultLeft = left + changeX * Math.sin((Math.PI / 2) * rate);

          setPosition(resultLeft);

          if (rate !== 1) {
            cancelLabel = requestAnimationFrame(step);
          }
        };
        step();

        cancelAnimationRef.current = () => {
          cancelAnimationFrame(cancelLabel);
        };

        return cancelAnimationRef.current;
      }
    },
    [setPosition]
  );

  // 平滑滚动到指定位置
  const scrollTo = useCallback(
    (targetLeft, duration = 300) => {
      const viewDom = viewRef.current;
      const contentDom = contentRef.current;

      if (viewDom && contentDom) {
        // 取消当前正在进行的动画
        if (cancelAnimationRef.current) {
          cancelAnimationRef.current();
        }

        const maxLeft = 0;
        const minLeft = -(contentDom.offsetWidth - viewDom.offsetWidth);

        // 确保目标位置在有效范围内
        const clampedTargetLeft = Math.min(
          maxLeft,
          Math.max(minLeft, -targetLeft)
        );

        const currentLeft = positionRef.current; // 当前位置
        const changeX = clampedTargetLeft - currentLeft; // 需要移动的距离

        // 如果已经在目标位置，直接返回
        if (Math.abs(changeX) < 0.1) {
          return;
        }

        const startTime = performance.now();

        let cancelLabel = null;
        const step = function () {
          const rate = Math.min(1, (performance.now() - startTime) / duration); // 时间进度（0 - 1）

          const resultLeft =
            currentLeft + changeX * Math.sin((Math.PI / 2) * rate);

          setPosition(resultLeft);

          if (rate !== 1) {
            cancelLabel = requestAnimationFrame(step);
          }
        };
        step();

        cancelAnimationRef.current = () => {
          cancelAnimationFrame(cancelLabel);
        };

        return cancelAnimationRef.current;
      }
    },
    [setPosition]
  );

  // 鼠标拖动滚动
  useEffect(() => {
    const viewDom = viewRef.current;
    const contentDom = contentRef.current;
    let scrollLeft = positionRef.current;

    let ox = 0;
    let timer = null;

    const downAction = (e) => {
      // 非左键点击
      if (!isTouchDevice() && e.button !== 0) {
        return;
      }

      if (e.type === "touchstart") {
        e = e.touches[0];
      } else {
        e.preventDefault(); // 防止默认事件
      }

      let isMouseDown = true;
      ox = e.clientX;
      let left = (scrollLeft = positionRef.current);
      const maxLeft = padding;
      const minLeft = -(contentDom.offsetWidth - viewDom.offsetWidth + padding);

      let firstX = e.clientX;
      let lastX = e.clientX;
      let lastTime = performance.now();

      // mouse move
      const moveAction = (e) => {
        if (e.type === "touchmove") {
          e = e.touches[0];
        }

        if (isMouseDown) {
          const dx = e.clientX - ox;
          left = scrollLeft + dx;

          if (Math.abs(dx) >= 3) {
            setDragging(true);

            if (left >= maxLeft) {
              left = maxLeft;
            } else if (left <= minLeft) {
              left = minLeft;
            }

            clearTimeout(timer);
            timer = setTimeout(() => {
              firstX = e.clientX;
              lastTime = performance.now();
            }, 50);

            lastX = e.clientX;
            setPosition(left);
          }
        }
      };

      // mouse up
      const endAction = (e) => {
        isMouseDown = false;
        setDragging(false);

        positionRef.current = left;

        const distance = lastX - firstX;
        const duration = performance.now() - lastTime;
        const speed = distance / duration;

        if (left > 0) {
          onSwitch({ distance: -left, duration: 400 });
        } else if (left < minLeft + padding) {
          onSwitch({
            distance: minLeft - left,
            duration: 400,
          });
        } else if (Math.abs(speed) >= 0.5) {
          onSwitch({ distance: speed * 500 });
        }

        // 释放绑定事件
        releaseMove();
        releaseEnd();
      };

      // 绑定事件
      const releaseMove = dragEvent.move(document, moveAction);
      const releaseEnd = dragEvent.end(document, endAction);
    };
    const releaseStart = dragEvent.start(viewDom, downAction, true);

    // 发生拖拽后取消点击事件
    const clickAction = (e) => {
      if (Math.abs(e.clientX - ox) > 3) {
        e.stopPropagation();
        e.preventDefault();
      }
    };
    viewDom.addEventListener("click", clickAction, true);

    return () => {
      releaseStart();
      viewDom.removeEventListener("click", clickAction, true);
      clearTimeout(timer);
    };
  }, [padding, onSwitch, setPosition]);

  // 初始化状态
  useEffect(() => {
    setSwiperStatus();
  }, [children, setSwiperStatus]);

  useEffect(() => {
    const viewDom = viewRef.current;

    // viewDom 大小变更时重新监听是否需要展示箭头，ResizeObserver Chrome浏览器支持>=65
    let resizeObserver = null;
    if (window.ResizeObserver) {
      resizeObserver = new ResizeObserver(() => {
        onSwitch({ distance: 0 });
      });
    }

    if (viewDom) {
      resizeObserver?.observe(viewDom);
    }

    return () => {
      if (viewDom) {
        resizeObserver?.disconnect();
      }
    };
  }, [onSwitch]);

  // Wheel event
  useEffect(() => {
    const viewDom = viewRef.current;
    const contentDom = contentRef.current;

    const scrollAction = (e) => {
      if (dragging) {
        return false;
      }

      if (e.deltaX) {
        e.preventDefault();
        const left = positionRef.current;
        const minLeft = -(contentDom.offsetWidth - viewDom.offsetWidth);
        const maxLeft = 0;

        const deltaX = e.shiftKey ? e.deltaX / 1 : e.deltaX;
        let resultLeft = left - deltaX;
        resultLeft = Math.min(maxLeft, Math.max(resultLeft, minLeft));
        setPosition(resultLeft);
      }
    };

    viewDom.addEventListener("wheel", scrollAction, true);
    return () => {
      viewDom.removeEventListener("wheel", scrollAction, true);
    };
  }, [padding, onSwitch, dragging, setPosition]);

  useImperativeHandle(ref, () => {
    return {
      reset() {
        setPosition(0);
      },
      onGoPage,
      scrollTo,
      getScrollDom: () => viewRef.current,
    };
  });

  return (
    <div
      {...rest}
      className={classNames([
        styles.hb_swiper,
        className,
        {
          [styles.hb_swiper_page]: type === "page",
          [styles.hide_controls]: hideControls,
          [styles.hide_gradient]: hideGradient,
        },
        start ? styles.start : "",
        end ? styles.end : "",
        dragging ? styles.dragging : "",
      ])}
      draggable={false}
      style={style}
    >
      {/* 条件渲染按钮 - 只有在不隐藏控制时才显示 */}
      {!hideControls && (
        <>
          {/* previous button */}
          <div
            className={classNames([
              styles.swiper_button_prev,
              iconClassName,
              "left",
            ])}
            onClick={() => onSwitch({ direction: 1 })}
          >
            <HBIcon type="ic_chevron_left" />
          </div>

          {/* next button */}
          <div
            className={classNames([
              styles.swiper_button_next,
              iconClassName,
              "right",
            ])}
            onClick={() => onSwitch({ direction: -1 })}
          >
            <HBIcon type="ic_chevron_right" />
          </div>
        </>
      )}

      {/* content */}
      <div className={styles.swiper_view} ref={viewRef}>
        <div
          className={classNames(styles.swiper_content, contentClassName)}
          // style={{ padding: `0 ${padding}px` }}
          ref={contentRef}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

export default forwardRef(HBSwiper);
